import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../Shared/Formatters';

/**
 * One card per series. First card is the baseline; subsequent cards show
 * delta vs the baseline (absolute + %). Useful for at-a-glance "who's up,
 * who's down" before the user dives into the chart.
 */
export default function ResultsKpiStrip({ seriesData, metric, palette }) {
    const { t } = useTranslation();
    if (!Array.isArray(seriesData) || seriesData.length === 0) return null;

    const fld = metricFieldKey(metric);
    const totals = seriesData.map(s => (s.series || []).reduce((a, r) => a + (r[fld] || 0), 0));
    const baseline = totals[0] || 0;
    const fmt = metric === 'transactions' ? (v) => Math.round(v).toLocaleString() : formatMoney;

    return (
        <div style={strip}>
            {seriesData.map((s, i) => {
                const total = totals[i];
                const delta = i === 0 ? null : total - baseline;
                const pct = i === 0 ? null : (baseline === 0 ? null : (delta / baseline) * 100);
                const up = delta != null && delta >= 0;
                return (
                    <div key={i} style={card}>
                        <div style={head}>
                            <span style={{ ...swatch, background: palette[i] }} />
                            <div style={{ fontSize: 12, color: 'var(--v3-text-muted)' }}>
                                {label(s)}
                            </div>
                        </div>
                        <div style={value}>{fmt(total)}</div>
                        {delta != null && (
                            <div style={{
                                fontSize: 12,
                                color: up ? 'var(--v3-green, #10B981)' : 'var(--v3-red, #EF4444)',
                            }}>
                                {up ? '▲' : '▼'} {fmt(Math.abs(delta))}
                                {pct != null && ` (${up ? '+' : ''}${pct.toFixed(1)}%)`}
                                <span style={{ color: 'var(--v3-text-muted)', marginLeft: 6 }}>{t('vs baseline')}</span>
                            </div>
                        )}
                        {i === 0 && (
                            <div style={{ fontSize: 11, color: 'var(--v3-text-muted)' }}>{t('baseline')}</div>
                        )}
                        {s.dateRange && (
                            <div style={{ fontSize: 10, color: 'var(--v3-text-muted)', marginTop: 4 }}>
                                {s.dateRange.from} → {s.dateRange.to}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function metricFieldKey(metric) {
    if (metric === 'transactions') return 'trans_count';
    if (metric === 'revenue') return 'revenue_share_amount';
    return 'sales';
}

function label(s) {
    return s.label || `${s.type}: ${s.id}`;
}

const strip = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    marginTop: 16,
};
const card = {
    padding: 14,
    borderRadius: 10,
    border: '1px solid var(--v3-glass-border)',
    background: 'var(--v3-bg-surface)',
};
const head = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 };
const swatch = { width: 10, height: 10, borderRadius: 3, flexShrink: 0 };
const value = { fontSize: 20, fontWeight: 600, color: 'var(--v3-text-primary)', marginBottom: 4 };
